/**
 * Signed Off tab — cases the accountant has already signed off.
 *
 * NOTE: No dedicated backend endpoint exists for "signed off cases". This tab
 * filters the existing /my-cases response on workflow_status === "signed_off"
 * (or assigned_queue === "signed_off"). If the backend never surfaces signed
 * cases via my-cases, this tab simply shows empty — deliberate scope tightness
 * for Task 4. Adding a dedicated `/accountant/signed-off` endpoint is a
 * follow-up if / when the list proves sparse in production.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../utils/api';
import { AccountantTabProps, CaseSummary, QueueResponse } from './types';
import AccountantCaseRow from './AccountantCaseRow';
import TabShell, { StatusFilterValue } from './TabShell';
import { useCaseFilter } from './useCaseFilter';

// Predicate: `signed_off` (boolean set by POST /api/v1/case-signoff/*) is the
// authoritative field. `workflow_status === 'signed_off'` is kept as a
// defensive fallback in case any older/legacy records use that shape — but the
// backend never writes it post-sign-off today.
const isSignedOff = (c: CaseSummary) =>
  c.signed_off === true || c.workflow_status === 'signed_off';

export default function SignedOffTab({
  selectedCaseId,
  onSelectCase,
  currentAccountantId,
  onVisibleCaseIdsChange,
}: AccountantTabProps) {
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // TODO: This piggybacks on /my-cases with a 50-row cap and then filters
      // client-side. Past 50 signed cases per accountant this will silently
      // drop rows. Swap to a dedicated `/accountant/signed-off` endpoint (or
      // add a `signed_off=true` query filter on `/my-cases`) before a single
      // accountant can realistically accumulate that many signed cases.
      const data = await api.get<QueueResponse>(
        `/api/v1/queue/accountant/my-cases?page=${page}&page_size=50`,
      );
      const all = Array.isArray(data.cases) ? data.cases : [];
      const signed = all.filter(isSignedOff);
      setCases(signed);
      setTotalCount(signed.length);
      setTotalPages(1); // client-side pagination would be a follow-up
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load signed-off cases');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const lastNotifiedRef = useRef<string>('');
  useEffect(() => {
    const ids = cases.map((c) => c.id);
    const key = ids.join('|');
    if (key !== lastNotifiedRef.current) {
      lastNotifiedRef.current = key;
      onVisibleCaseIdsChange?.(ids);
    }
  }, [cases, onVisibleCaseIdsChange]);

  const filteredCases = useCaseFilter(cases, searchQuery, statusFilter);

  return (
    <TabShell
      tabPanelId="tabpanel-signed"
      tabPanelLabelledBy="tab-signed"
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      heading="Signed Off Cases"
      filteredCount={filteredCases.length}
      totalCount={totalCount}
      loading={loading}
      error={error}
      onDismissError={() => setError(null)}
      onRetry={fetchData}
      page={page}
      totalPages={totalPages}
      onPrevPage={() => setPage(Math.max(1, page - 1))}
      onNextPage={() => setPage(Math.min(totalPages, page + 1))}
      isEmpty={filteredCases.length === 0}
      emptyMessage={
        searchQuery || statusFilter !== 'all'
          ? 'No cases match your search criteria. Try adjusting your filters.'
          : 'No signed-off cases yet. Cases appear here after you sign them off from the My Cases tab.'
      }
    >
      {filteredCases.map((caseItem) => (
        <AccountantCaseRow
          key={caseItem.id}
          caseItem={caseItem}
          mode="signed"
          isSelected={selectedCaseId === caseItem.id}
          // Strict select (BUG-4) — see QueueTab for rationale.
          onSelect={(id) => onSelectCase(id)}
          currentAccountantId={currentAccountantId}
        />
      ))}
    </TabShell>
  );
}
